import { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import ComicCard from "@/components/base/ComicCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";
import { comicCategories, getComicsByCategory } from "@/mocks/comicData";

export default function CategoryPage() {
  const { slug = "all" } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug);
  const [sortBy, setSortBy] = useState<string>("popular");

  const currentCategory = useMemo(
    () => comicCategories.find((c) => c.slug === activeSlug),
    [activeSlug]
  );

  const comics = useMemo(() => {
    let result = getComicsByCategory(activeSlug);
    if (sortBy === "rating") {
      result = [...result].sort((a, b) => b.rating - a.rating);
    } else if (sortBy === "latest") {
      result = [...result].sort(
        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      );
    } else if (sortBy === "views") {
      result = [...result].sort((a, b) => b.totalViews - a.totalViews);
    }
    return result;
  }, [activeSlug, sortBy]);

  const handleCategoryClick = (newSlug: string) => {
    setActiveSlug(newSlug);
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  const sortOptions = [
    { value: "popular", label: "综合排序" },
    { value: "latest", label: "最新更新" },
    { value: "rating", label: "评分最高" },
    { value: "views", label: "人气最高" },
  ];

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: `${currentCategory?.name || "全部"}漫画 - 虚空韩漫网`,
            description: `虚空韩漫网${currentCategory?.name || "全部"}漫画分类，韩漫在线免费看下拉式阅读，共${comics.length}部作品。`,
            url: `https://xukongmanhua.com/category/${activeSlug}`,
            isPartOf: {
              "@type": "WebSite",
              name: "虚空韩漫网",
              url: "https://xukongmanhua.com",
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://xukongmanhua.com" },
              { "@type": "ListItem", position: 2, name: "漫画分类", item: "https://xukongmanhua.com/category/all" },
              { "@type": "ListItem", position: 3, name: currentCategory?.name || "全部" },
            ],
          }),
        }}
      />
      <main className="min-h-screen bg-background-50">
        <div className="pt-20 pb-6 bg-background-50">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <Breadcrumb />
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mt-3 font-heading">
              {currentCategory?.name || "全部"}漫画
            </h1>
            <p className="text-sm text-foreground-500 mt-1">
              共 {comics.length} 部作品
            </p>
          </div>
        </div>

        <div className="max-w-[1440px] mx-auto px-4 md:px-6 pb-12">
          <div className="flex flex-wrap items-center gap-2 mb-6">
            {comicCategories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => handleCategoryClick(cat.slug)}
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeSlug === cat.slug
                    ? "bg-primary-500 text-white"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                <i className={`${cat.icon} text-sm w-4 h-4 flex items-center justify-center`}></i>
                {cat.name}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 mb-6 flex-wrap">
            {sortOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setSortBy(opt.value)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                  sortBy === opt.value
                    ? "bg-foreground-950 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {comics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
              {comics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 bg-background-100 rounded-full flex items-center justify-center">
                <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500 text-sm">该分类暂无漫画</p>
            </div>
          )}
        </div>
      </main>
    </>
  );
}